import React, { useState, useEffect } from "react";
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore.js";
import captureException from "@/func/sentryException";
import { buildApplePayCustomer, missingShippingFields, missingAddressError } from "@/utils/applePayContact";
import s from "@/components/Buttons/ExpressCheckoutButtons.module.scss";

const ApplePayButton = ({ height, onApplePaySubmit, requirePolicy }) => {
  const [isAvailable, setIsAvailable] = useState(false);

  const setIsApplePayAvailable = useTemplateStore((s) => s.setIsApplePayAvailable); // Browser defines this
  const handlePaymentMethodChange = useTemplateStore((s) => s.handlePaymentMethodChange); // For Accordion Toggle

  const { currency_code, country_code, paymentsConfig } = useProductData.getState();
  const { dynamicDescriptor, applePayConfigured } = paymentsConfig;
  const { cartTotal } = useCartData.getState();

  useEffect(() => {
    setIsApplePayAvailable(Boolean(isAvailable && applePayConfigured));
  }, [isAvailable, applePayConfigured]);

  useEffect(() => {
    try {
      if (applePayConfigured && window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
        setIsAvailable(true);
      }
    } catch (err) {
      console.error("Apple Pay availability check failed:", err);
    }
  }, []);

  if (!isAvailable || !applePayConfigured) {
    return null;
  }
  const handleAdyenApplePaySubmit = async () => {
    // handlePaymentMethodChange("applepay"); // For Accordion Toggle

    const paymentRequest = {
      currencyCode: currency_code || "USD",
      countryCode: country_code || "US",
      total: {
        label: dynamicDescriptor, // "Checkoutera",
        amount: cartTotal || "0.00",
      },

      supportedNetworks: ["visa", "masterCard", "amex", "discover", "JCB"],
      merchantCapabilities: ["supports3DS"],
      requiredShippingContactFields: ["email", "phone", "postalAddress", "name"],
    };

    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = async (event) => {
      const r = await fetch("/wp-json/checkout/v1/order/applepay/validate", { method: "POST" });
      const merchantSession = await r.json();
      session.completeMerchantValidation(merchantSession);
    };

    session.oncancel = (event) => {
      // TODO: Add cancel handler. Maybe we can show modal or error message and let user know to try different payment method?
    };

    session.onpaymentauthorized = async (event) => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => {
          // Max 30 seconds to pay, otherwise - cancel
          controller.abort();
        }, 30000);

        const customer = buildApplePayCustomer(event.payment);
        const missing = missingShippingFields(customer);

        // Never charge for an order we cannot ship. Returning an error keeps the
        // Apple Pay sheet open so the customer can correct their address.
        if (missing.length > 0) {
          clearTimeout(timeout);
          captureException(new Error(`Apple Pay contact missing required fields: ${missing.join(", ")}`), {
            tags: { type: "Javascript error", error: "apple_pay_incomplete_shipping_contact" },
            extra: { missing_fields: missing },
          });
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors: [missingAddressError()],
          });
          return;
        }

        const applePayData = {
          apple_pay_token: event.payment.token,
          customer,
        };

        // Call the form handler with Apple Pay specific data
        if (onApplePaySubmit) {
          await onApplePaySubmit(applePayData, { signal: controller.signal });
          clearTimeout(timeout);

          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          // Redirect to next page / upsell
        } else {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          // TODO: Show error message
        }
      } catch (e) {
        console.error("Payment authorization error:", e);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        // TODO: Show error message (choose different payment method)
      }
    };

    session.begin();
  };

  const handleClick = async () => {
    handlePaymentMethodChange("applepay");

    if (typeof requirePolicy === "function") {
      const ok = await requirePolicy();
      if (!ok) return;
    }

    await handleAdyenApplePaySubmit();
  };

  return (
    <div className={s["applepay-container"]}>
      <button
        type="button"
        onClick={handleClick}
        style={{
          appearance: "-apple-pay-button",
          WebkitAppearance: "-apple-pay-button",
          applePayButtonStyle: "black",
          width: "100%",
          height: height || 56,
          borderRadius: 6,
          border: "none",
          cursor: "pointer",
        }}
      />
    </div>
  );
};

export default ApplePayButton;
